import { ApiError } from "@/lib/apiClient";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";
import {
  PermissionUser,
  GetPermissionUsersParams,
} from "@/services/permissionService";

export interface PermissionState {
  items: PermissionUser[];
  loaded: boolean;
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };
  fetchParams: GetPermissionUsersParams;
  actionState: {
    loading: boolean;
    error: ApiError | null;
    successMessage: string | null;
  };
}

const initialState: PermissionState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  fetchParams: {
    page: 1,
    page_size: 10,
    sort_by: "created_at",
    sort_order: "desc",
  },
  actionState: {
    loading: false,
    error: null,
    successMessage: null,
  },
};

const permissionSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    fetchPermissionUsersRequest: (
      state,
      action: PayloadAction<{ storeId: string; params?: GetPermissionUsersParams }>,
    ) => {
      state.loading = true;
      state.error = null;
      state.fetchParams = { ...state.fetchParams, ...(action.payload.params || {}) };
    },
    fetchPermissionUsersSuccess: (
      state,
      action: PayloadAction<{
        data: PermissionUser[];
        pagination: { page: number; total: number; pageSize: number; totalPages: number };
      }>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.items = action.payload.data;
      state.pagination = action.payload.pagination;
    },
    fetchPermissionUsersFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },

    updatePermissionUserAccessRequest: (
      state,
      _action: PayloadAction<{
        storeId: string;
        userId: string;
        accessLevel: "seller" | "wholesaler" | "order_manager" | "staff";
      }>,
    ) => {
      state.actionState.loading = true;
      state.actionState.error = null;
      state.actionState.successMessage = null;
    },
    updatePermissionUserAccessSuccess: (
      state,
      action: PayloadAction<PermissionUser>,
    ) => {
      state.actionState.loading = false;
      state.actionState.successMessage = "User role updated successfully";
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    updatePermissionUserAccessFailure: (state, action: PayloadAction<ApiError>) => {
      state.actionState.loading = false;
      state.actionState.error = action.payload;
    },

    removePermissionUserRequest: (
      state,
      _action: PayloadAction<{ storeId: string; userId: string }>,
    ) => {
      state.actionState.loading = true;
      state.actionState.error = null;
      state.actionState.successMessage = null;
    },
    removePermissionUserSuccess: (
      state,
      action: PayloadAction<{ userId: string }>,
    ) => {
      state.actionState.loading = false;
      state.actionState.successMessage = "User removed successfully";
      state.items = state.items.filter((item) => item.id !== action.payload.userId);
      state.pagination.total = Math.max(0, state.pagination.total - 1);
    },
    removePermissionUserFailure: (state, action: PayloadAction<ApiError>) => {
      state.actionState.loading = false;
      state.actionState.error = action.payload;
    },

    clearPermissionActionState: (state) => {
      state.actionState = {
        loading: false,
        error: null,
        successMessage: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchPermissionUsersRequest,
  fetchPermissionUsersSuccess,
  fetchPermissionUsersFailure,
  updatePermissionUserAccessRequest,
  updatePermissionUserAccessSuccess,
  updatePermissionUserAccessFailure,
  removePermissionUserRequest,
  removePermissionUserSuccess,
  removePermissionUserFailure,
  clearPermissionActionState,
} = permissionSlice.actions;

export default permissionSlice.reducer;
